import { ReactNode, useEffect, useRef, useState } from 'react';
import uPlot, { AlignedData, Options } from 'uplot';
import 'uplot/dist/uPlot.min.css';
import {
  formatTooltipAlertText,
  isChartExcludedTarget,
  resolveTooltipAtPoint,
} from './chart-tooltip-utils';
import Tooltip from './tooltip';
import { TooltipData } from './types';

interface ChartProps {
  options: Options;
  data: AlignedData;
  width: string;
  height: string;
  timezone?: string;
  /** moment format string for the tooltip timestamp (default: full datetime). */
  tooltipTimestampFormat?: string;
  /** When true, shows a pointer cursor while hovering near a single line (single tooltip mode). */
  pointerOnSingleLineHover?: boolean;
  renderSingleTooltip?: (data: TooltipData) => ReactNode;
  onPointClick?: (data: TooltipData) => void;
  onTooltipData?: (data: TooltipData) => void;
}

const Chart = (props: ChartProps) => {
  const {
    options,
    data,
    width = '400px',
    height = '300px',
    timezone,
    tooltipTimestampFormat = 'YYYY-MM-DD HH:mm:ss',
    pointerOnSingleLineHover = false,
    renderSingleTooltip,
    onPointClick,
    onTooltipData,
  } = props;
  const chartRef = useRef<HTMLDivElement>(null);
  const uplotRef = useRef<uPlot | null>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [tooltipData, setTooltipData] = useState<TooltipData | null>(null);
  const [showTooltip, setShowTooltip] = useState(false);

  useEffect(() => {
    if (chartRef.current) {
      // Destroy existing chart if it exists
      if (uplotRef.current) {
        uplotRef.current.destroy();
        uplotRef.current = null;
      }

      // Create new chart with updated data/options
      try {
        const chartOptions = {
          ...options,
          hooks: {
            ...options.hooks,
          },
        };

        uplotRef.current = new uPlot(chartOptions, data, chartRef.current);
        const uOverDiv = uplotRef.current.root.querySelector('.u-over') as HTMLElement | null;

        const setPlotCursor = (cursor: string) => {
          if (pointerOnSingleLineHover && uOverDiv) {
            uOverDiv.style.cursor = cursor;
          }
        };

        const getTooltipDimensions = () => {
          if (!tooltipRef.current) {
            return { tooltipWidth: 200, tooltipHeight: 100 };
          }
          const tooltipRect = tooltipRef.current.getBoundingClientRect();
          return {
            tooltipWidth: tooltipRect.width || 200,
            tooltipHeight: tooltipRect.height || 100,
          };
        };

        const handleMouseMove = (event: MouseEvent) => {
          if (!uplotRef.current) {
            return;
          }

          if (isChartExcludedTarget(event.target)) {
            setShowTooltip(false);
            setPlotCursor('');
            return;
          }

          if (!uOverDiv) {
            return;
          }

          const rect = uOverDiv.getBoundingClientRect();
          const mouseX = event.clientX - rect.left;
          const mouseY = event.clientY - rect.top;
          const { tooltipWidth, tooltipHeight } = getTooltipDimensions();

          const tooltipInfo = resolveTooltipAtPoint(uplotRef.current, {
            timezone,
            tooltipTimestampFormat,
            mouseX,
            mouseY,
            tooltipWidth,
            tooltipHeight,
          });

          if (!tooltipInfo) {
            setShowTooltip(false);
            setPlotCursor('');
            return;
          }

          setPlotCursor(tooltipInfo.mode === 'single' ? 'pointer' : '');
          setTooltipData(tooltipInfo);
          setShowTooltip(true);
          onTooltipData?.(tooltipInfo);
        };

        const handleClick = (event: MouseEvent) => {
          if (!uplotRef.current || isChartExcludedTarget(event.target)) {
            return;
          }

          const uOverDiv = uplotRef.current.root.querySelector('.u-over') as HTMLElement;
          if (!uOverDiv) {
            return;
          }

          const rect = uOverDiv.getBoundingClientRect();
          const mouseX = event.clientX - rect.left;
          const mouseY = event.clientY - rect.top;

          const tooltipInfo = resolveTooltipAtPoint(uplotRef.current, {
            timezone,
            tooltipTimestampFormat,
            mouseX,
            mouseY,
          });

          if (tooltipInfo) {
            if (onPointClick) {
              onPointClick(tooltipInfo);
            } else {
              window.alert(formatTooltipAlertText(tooltipInfo));
            }
          }
        };

        const handleMouseLeave = () => {
          setShowTooltip(false);
          setPlotCursor('');
        };

        const currentChartRef = chartRef.current;
        currentChartRef.addEventListener('mousemove', handleMouseMove);
        currentChartRef.addEventListener('click', handleClick);
        currentChartRef.addEventListener('mouseleave', handleMouseLeave);

        return () => {
          if (currentChartRef) {
            currentChartRef.removeEventListener('mousemove', handleMouseMove);
            currentChartRef.removeEventListener('click', handleClick);
            currentChartRef.removeEventListener('mouseleave', handleMouseLeave);
          }
        };
      } catch (error) {
        console.error('Error creating chart:', error);
      }
    }

    return () => {
      if (uplotRef.current) {
        uplotRef.current.destroy();
        uplotRef.current = null;
      }
    };
  }, [
    options,
    data,
    timezone,
    tooltipTimestampFormat,
    pointerOnSingleLineHover,
    onPointClick,
    onTooltipData,
  ]);

  return (
    <div style={{ position: 'relative', width: width, height: height }}>
      <div ref={chartRef} style={{ width: '100%', height: '100%' }} />
      <Tooltip
        ref={tooltipRef}
        data={tooltipData}
        visible={showTooltip}
        renderSingleTooltip={renderSingleTooltip}
      />
    </div>
  );
};

export default Chart;
